type CardStepLookup = Record<string, { stepId: string | null | undefined } | undefined>;
type StepBucketCard = {
  data: { stepId?: string | null; doneAt?: unknown };
  isVisible?: boolean;
};
type StepBucketLookup = Record<string, StepBucketCard | undefined>;

type SessionStepLike = { id: string; name?: string };

export function getBatchMoveSuccessCount({
  uniqueCardIds,
  failureCount,
}: {
  uniqueCardIds: string[];
  failureCount: number;
}): number {
  return uniqueCardIds.length - failureCount;
}

export function getMoveStepName({
  stepId,
  steps,
  getStepNameById,
  triageStepId,
}: {
  stepId: string | null;
  steps: SessionStepLike[];
  getStepNameById: (stepId: string) => string;
  triageStepId: string;
}): string {
  if (stepId === null) {
    return getStepNameById(triageStepId);
  }

  const step = steps.find((item) => item.id === stepId);
  return step?.name ?? 'Unassigned';
}

export function getCardIdsWithStepMoveFirst(
  cardIds: string[],
  cardStepLookup: CardStepLookup,
  movedCardId: string,
  targetStepId: string | null,
): string[] {
  const nextCardIds = cardIds.filter((cardId) => cardId !== movedCardId);
  const insertIndex = nextCardIds.findIndex(
    (cardId) => (cardStepLookup[cardId]?.stepId ?? null) === targetStepId,
  );

  if (insertIndex === -1) {
    nextCardIds.push(movedCardId);
    return nextCardIds;
  }

  nextCardIds.splice(insertIndex, 0, movedCardId);
  return nextCardIds;
}

export function buildStepCardBuckets({
  cardIds,
  steps,
  cards,
  visibleOnly = false,
}: {
  cardIds: string[];
  steps: SessionStepLike[];
  cards: StepBucketLookup;
  visibleOnly?: boolean;
}): {
  inProgressByStep: Map<string | null, string[]>;
  doneCardIds: string[];
} {
  const inProgressByStep = new Map<string | null, string[]>();
  const doneCardIds: string[] = [];

  inProgressByStep.set(null, []);
  steps.forEach((step) => {
    inProgressByStep.set(step.id, []);
  });

  cardIds.forEach((cardId) => {
    const card = cards[cardId];
    if (!card) return;
    if (visibleOnly && !card.isVisible) return;

    if (card.data.doneAt) {
      doneCardIds.push(cardId);
      return;
    }

    const stepId = card.data.stepId ?? null;
    const existing = inProgressByStep.get(stepId) ?? [];
    existing.push(cardId);
    inProgressByStep.set(stepId, existing);
  });

  return {
    inProgressByStep,
    doneCardIds: doneCardIds.reverse(),
  };
}
